import { useState, type FormEvent } from "react";

import { usePlaylists, useSchedules } from "../../api/hooks.js";
import { createFeed, type FeedCreateInput } from "../../api/feeds.js";
import { Button } from "../../design-system/button.js";
import {
  Field,
  NumberInput,
  Select,
  Switch,
  TextInput,
} from "../../design-system/form.js";
import { Modal } from "../../design-system/modal.js";
import { Notice } from "../../design-system/surfaces.js";

/**
 * A new feed.
 *
 * A feed must reference a playlist it can play — `default_playlist_id` is required by
 * the contract, not by this form's taste — so with no playlists in the library there
 * is nothing to point a feed at, and the form says exactly that instead of posting a
 * request the API would refuse.
 *
 * Everything the form does not send is the schema's own baseline. It carries no
 * default rotation seed, no default loudness and no default delivery, because those
 * are stated in the database and a copy of them here would be a second authority.
 */
export function CreateFeedDialog({
  onClose,
  onCreated,
  open,
}: {
  onClose: () => void;
  onCreated: () => void;
  open: boolean;
}) {
  const playlists = usePlaylists(open);
  const schedules = useSchedules(open);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [defaultPlaylistId, setDefaultPlaylistId] = useState("");
  const [fallbackPlaylistId, setFallbackPlaylistId] = useState("");
  const [scheduleId, setScheduleId] = useState("");
  const [crossfadeMs, setCrossfadeMs] = useState("2000");
  const [enabled, setEnabled] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const available = (playlists.data ?? []).filter(
    (playlist) => playlist.enabled,
  );
  const ready = name.trim() !== "" && defaultPlaylistId !== "" && !busy;

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!ready) return;
    setBusy(true);
    setError(null);
    const input: FeedCreateInput = {
      name: name.trim(),
      description: description.trim(),
      default_playlist_id: defaultPlaylistId,
      fallback_playlist_id:
        fallbackPlaylistId === "" ? null : fallbackPlaylistId,
      schedule_id: scheduleId === "" ? null : scheduleId,
      enabled,
    };
    const crossfade = Number(crossfadeMs);
    if (Number.isFinite(crossfade) && crossfade >= 0) {
      input.crossfade_ms = Math.round(crossfade);
    }
    createFeed(input)
      .then(() => {
        setName("");
        setDescription("");
        setDefaultPlaylistId("");
        setFallbackPlaylistId("");
        setScheduleId("");
        onCreated();
        onClose();
      })
      .catch((cause: unknown) => {
        setError(
          cause instanceof Error ? cause.message : "The feed was not created.",
        );
      })
      .finally(() => {
        setBusy(false);
      });
  }

  return (
    <Modal
      description="Name it and choose what it plays. You'll set up its delivery after."
      onClose={onClose}
      open={open}
      title="New feed"
    >
      <form className="flex flex-col gap-5" noValidate onSubmit={submit}>
        <Field htmlFor="feed-name" label="Name">
          <TextInput
            autoFocus
            id="feed-name"
            onChange={(event) => {
              setName(event.target.value);
            }}
            placeholder="Late night"
            required
            value={name}
          />
        </Field>

        <Field htmlFor="feed-description" label="Description">
          <TextInput
            id="feed-description"
            onChange={(event) => {
              setDescription(event.target.value);
            }}
            value={description}
          />
        </Field>

        {playlists.loading ? null : available.length === 0 ? (
          <Notice tone="warning" title="No playlist to draw on">
            A feed plays from a playlist, and there is no enabled playlist to
            point one at.
          </Notice>
        ) : null}

        <div className="grid gap-4 sm:grid-cols-2">
          <Field htmlFor="feed-default-playlist" label="Default playlist">
            <Select
              disabled={available.length === 0}
              id="feed-default-playlist"
              onChange={(event) => {
                setDefaultPlaylistId(event.target.value);
              }}
              required
              value={defaultPlaylistId}
            >
              <option value="">Choose a playlist</option>
              {available.map((playlist) => (
                <option key={playlist.id} value={playlist.id}>
                  {playlist.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            hint="Plays when nothing else is available."
            htmlFor="feed-fallback-playlist"
            label="Fallback playlist"
          >
            <Select
              id="feed-fallback-playlist"
              onChange={(event) => {
                setFallbackPlaylistId(event.target.value);
              }}
              value={fallbackPlaylistId}
            >
              <option value="">None</option>
              {available.map((playlist) => (
                <option key={playlist.id} value={playlist.id}>
                  {playlist.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field
            hint="A schedule governs which playlist plays when."
            htmlFor="feed-schedule"
            label="Schedule"
          >
            <Select
              id="feed-schedule"
              onChange={(event) => {
                setScheduleId(event.target.value);
              }}
              value={scheduleId}
            >
              <option value="">None</option>
              {(schedules.data ?? [])
                .filter((schedule) => schedule.enabled)
                .map((schedule) => (
                  <option key={schedule.id} value={schedule.id}>
                    {schedule.name}
                  </option>
                ))}
            </Select>
          </Field>

          <Field
            hint="Must be shorter than the shortest track this feed plays."
            htmlFor="feed-crossfade"
            label="Crossfade"
          >
            <NumberInput
              id="feed-crossfade"
              min={0}
              onChange={(event) => {
                setCrossfadeMs(event.target.value);
              }}
              step={100}
              unit="ms"
              value={crossfadeMs}
            />
          </Field>
        </div>

        <Switch
          checked={enabled}
          description="Starts playing as soon as it's created."
          label="Enabled"
          onChange={setEnabled}
        />

        {error ? <Notice tone="danger">{error}</Notice> : null}

        <div className="flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button onClick={onClose} variant="ghost">
            Cancel
          </Button>
          <Button disabled={!ready} type="submit">
            {busy ? "Creating…" : "Create feed"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
